import {
  DEFAULT_SESSION_QUERY_SCOPE_TOKEN,
  queryKeys,
  withSessionQueryScope,
} from "~/lib/query-keys";
import { getBrowserPlatformApiClient } from "~/lib/trpc/browser-client";
import type { PlatformApiTrpcClient } from "~/lib/trpc/platform-api-adapter";
import { seedAssetUniverseFromCanonicalGroups } from "./db/collections";

export type AssetUniverseGraphAvailabilityFilter =
  | "indexed"
  | "glider_supported";

export const ASSET_UNIVERSE_CANONICAL_GRAPH_STALE_TIME_MS = 5 * 60_000;

/** Matches getCanonicalGraphsInputSchema's assetCanonicalIds upper bound. */
export const ASSET_UNIVERSE_CANONICAL_GRAPH_MAX_IDS = 50;

export function getAssetUniverseCanonicalGraphQueryOptions(input: {
  assetCanonicalIds: readonly string[];
  availabilityFilter: AssetUniverseGraphAvailabilityFilter;
  gcTimeMs?: number;
  platformApi?: PlatformApiTrpcClient;
  sessionScopeToken?: string;
  staleTimeMs?: number;
}) {
  const platformApi = input.platformApi ?? getBrowserPlatformApiClient();
  const assetCanonicalIds = [
    ...new Set(
      input.assetCanonicalIds
        .map((assetCanonicalId) => assetCanonicalId.trim())
        .filter((assetCanonicalId) => assetCanonicalId.length > 0),
    ),
  ].sort((left, right) => left.localeCompare(right));
  const sessionScope = {
    scopeToken: input.sessionScopeToken ?? DEFAULT_SESSION_QUERY_SCOPE_TOKEN,
  };
  const options = {
    enabled: assetCanonicalIds.length > 0,
    queryKey: withSessionQueryScope(
      queryKeys.assetDomain.canonicalGraphsByKey(
        [input.availabilityFilter, assetCanonicalIds.join("|")].join("::"),
      ),
      sessionScope,
    ),
    queryFn: async () => {
      const response =
        await platformApi.assetHydration.getCanonicalGraphs.query({
          assetCanonicalIds,
          availabilityFilter: input.availabilityFilter,
        });
      await seedAssetUniverseFromCanonicalGroups(response.items, {
        requestedCanonicalIds: assetCanonicalIds,
        sessionScopeToken: sessionScope.scopeToken,
        tokenizedEquityValuations: response.tokenizedEquityValuations,
        tokenizedEquityValuationsStatus:
          response.tokenizedEquityValuationsStatus,
        valuationAvailabilityFilter: input.availabilityFilter,
      });
      return response;
    },
    staleTime:
      input.staleTimeMs ?? ASSET_UNIVERSE_CANONICAL_GRAPH_STALE_TIME_MS,
  } as const;

  return input.gcTimeMs === undefined
    ? options
    : { ...options, gcTime: input.gcTimeMs };
}
